import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller } from '../../components/common';
import { formatBytes, formatRelative } from '../../lib/format';
import { IssueList, Kpi, joinParts } from '../../pages/dashboard/shared';
import { ConnectionChip, VersionCell, agPath, tunnelOf } from '../shared';
import {
  authProgress,
  automationIssues,
  connectedCount,
  outdatedAgents,
  useAutomationSummary,
  type AutomationSummary,
} from './summary';
import '../../pages/statistics/statistics.css';
import '../automation.css';

const TOP = 10;

function EnvironmentSection({ s }: { s: AutomationSummary }) {
  const target = s.target.id;
  const link = (path: string) => agPath(path, target);
  const connected = connectedCount(s);
  const offline = s.agents.length - connected;
  const outdated = outdatedAgents(s).length;
  const auth = authProgress(s);
  const channels = s.tunnels.reduce((n, t) => n + (t.isConnected ? t.activeChannels : 0), 0);
  const tenantOf = (id: string) => s.tunnels.find((t) => t.clientDbId === id)?.tenantName ?? '';
  // offline first (longest gone first), then by name
  const agents = s.agents.slice().sort((a, b) => {
    const ca = !!tunnelOf(s.tunnels, a.clientId)?.isConnected;
    const cb = !!tunnelOf(s.tunnels, b.clientId)?.isConnected;
    if (ca !== cb) return ca ? 1 : -1;
    return a.displayName.localeCompare(b.displayName);
  });
  const traffic = s.traffic.filter((r) => r.toAgent + r.fromAgent > 0).slice(0, TOP);
  return (
    <section className="oph-dash-section" aria-label={`Automation ${s.target.label}`} data-target={target}>
      <div className="oph-dash-section-head">
        <h2>{s.target.label}</h2>
      </div>
      {s.error ? (
        <ErrorBox error={s.error} />
      ) : (
        <>
          {s.partErrors.length > 0 && <ErrorBox error={s.partErrors.join(' · ')} />}
          <div className="nyk-kpis">
            <Kpi
              label="Connected"
              value={`${connected}/${s.agents.length}`}
              sub="agents with a tunnel"
              to={link('/automation/agents?state=connected')}
            />
            <Kpi
              label="Offline"
              value={offline}
              tone={offline ? 'red' : undefined}
              to={link('/automation/agents?state=offline')}
            />
            <Kpi
              label="Update available"
              value={outdated}
              sub={s.latest ? `latest ${s.latest.version}` : undefined}
              tone={outdated ? 'amber' : undefined}
              to={link('/automation/agents?state=outdated')}
            />
            <Kpi
              label="Routes"
              value={s.routes.length}
              sub={`${s.routes.filter((r) => r.direction === 'reverse').length} reverse`}
              to={link('/automation/routes')}
            />
            <Kpi label="Open channels" value={channels} to={link('/automation/traffic')} />
            <Kpi
              label="On AUTH"
              value={`${auth.onAuth}/${s.agents.length}`}
              sub={joinParts([
                auth.authOnly ? `${auth.authOnly} AUTH only` : null,
                auth.enrolled ? `${auth.enrolled} enrolled, waiting` : null,
                `${auth.secret} on the secret`,
              ])}
              to={link('/automation/agents?state=auth')}
            />
            <Kpi
              label="Need a site visit"
              value={auth.siteVisit}
              sub="no bootstrapper or a container"
              tone={auth.siteVisit ? 'amber' : undefined}
              to={link('/automation/agents?state=site')}
            />
          </div>
          <div className="bd-grid">
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Agents</h2>
                <span className="nyk-ui-hint">{s.agents.length}</span>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="ag-dash-agents">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Tunnel</th>
                      <th>Version</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.slice(0, TOP).map((a) => {
                      const t = tunnelOf(s.tunnels, a.clientId);
                      const seen = s.lastSeen.get(a.clientId);
                      return (
                        <tr key={a.id} data-agent={a.clientId}>
                          <td className="nyk-card-title">
                            <Link to={link(`/automation/agents/${encodeURIComponent(a.id)}`)}>
                              {a.displayName}
                            </Link>
                            <div className="nyk-ui-hint">{tenantOf(a.id)}</div>
                          </td>
                          <td data-label="Tunnel">
                            <ConnectionChip t={t} />
                            {!t?.isConnected && (
                              <div className="nyk-ui-hint">
                                {seen ? `seen ${formatRelative(seen)}` : 'not in 30 days'}
                              </div>
                            )}
                          </td>
                          <td data-label="Version">
                            <VersionCell
                              version={t ? s.infos.get(t.clientId)?.version : null}
                              latest={s.latest?.version}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroller>
              {!s.agents.length && <EmptyState title="No agents registered." />}
              {s.agents.length > TOP && (
                <p className="nyk-ui-hint">
                  <Link to={link('/automation/agents')}>All {s.agents.length} agents</Link>
                </p>
              )}
            </div>
            <div className="nyk-card">
              <div className="nyk-card-head">
                <h2>Busiest routes, 24 h</h2>
              </div>
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="ag-dash-traffic">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th className="nyk-ui-num">To agent</th>
                      <th className="nyk-ui-num">From agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traffic.map((r) => (
                      <tr key={r.routeId}>
                        <td className="nyk-card-title">{r.label}</td>
                        <td className="nyk-ui-num" data-label="To agent">
                          {formatBytes(r.toAgent)}
                        </td>
                        <td className="nyk-ui-num" data-label="From agent">
                          {formatBytes(r.fromAgent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
              {!traffic.length && <EmptyState title="No traffic in 24 h." />}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Automation dashboard: per environment the agents' tunnels and versions, offline ones, and route traffic. */
export function AutomationDashboardPage() {
  const res = useAutomationSummary();
  const list = res.data ?? [];
  return (
    <Page
      title="Automation"
      description="Warehouse agents per environment: connected or offline, their versions and disks, and the traffic through their routes. Cached for a minute."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading}
          onClick={res.reload}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {!res.enabled && <EmptyState card title="No automation access" hint="Needs ophub:automation:read." />}
      {res.enabled && !res.data && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {res.data && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <IssueList
            issues={automationIssues(list)}
            title="Automation issues"
            clear="No automation issues."
            testId="ag-issues"
          />
          {list.map((s) => (
            <EnvironmentSection key={s.target.id} s={s} />
          ))}
          {!list.length && (
            <EmptyState
              card
              title="No automation gateway configured"
              hint="Set AutomationGwUrl on an AuthTargets entry and the hub’s AutomationGw:Tokens:<id>."
            />
          )}
        </div>
      )}
    </Page>
  );
}
